/**
 * Revenue source WRITE endpoints — request schemas (Pattern B).
 *
 * Served by `apps/api/src/routes/accounting/revenue-sources.ts` under
 * `/api/v1/revenue-sources`. Writes to `revenue_sources` are backend-only
 * once the wave-2b lockdown migration (`20261001070733`) is
 * pushed; the org-member RLS policy keeps doing the authorization.
 *
 * `.strict()`: `organization_id` is the validated `:orgId`, `created_by` the
 * verified caller — neither is accepted from the body.
 */
import { z } from "zod";
import { calendarDateSchema } from "./dateWindow.js";
/**
 * `revenue_sources_source_type_check` values, minus `grant`: grants are funds
 * now (fund management P1, 20261006210000). The DB CHECK keeps the dead
 * `grant` value; the API no longer accepts it.
 */
export const revenueSourceTypeSchema = z.enum([
    "sponsor",
    "donation",
    "ticket_sales",
    "merchandise",
    "subscription",
    "other",
]);
/** `revenue_sources_status_check` values. */
export const revenueSourceStatusSchema = z.enum([
    "pending",
    "confirmed",
    "partially_received",
    "completed",
    "cancelled",
]);
const optionalText = z.string().nullable().optional();
const optionalDate = calendarDateSchema.nullable().optional();
const optionalAmount = z.number().nullable().optional();
const revenueSourceFields = {
    source_type: revenueSourceTypeSchema,
    source_name: z.string().trim().min(1).max(255),
    description: optionalText,
    amount_expected: optionalAmount,
    contract_start_date: optionalDate,
    contract_end_date: optionalDate,
    contact_person: optionalText,
    contact_email: optionalText,
    contact_phone: optionalText,
};
/**
 * POST /orgs/:orgId/revenue-sources. `project_id` must be a project of the
 * URL org (the table has no FK to projects, so the route checks it).
 * `amount_received` starts at 0 and `status` at 'pending'.
 */
export const createRevenueSourceSchema = z
    .object({
    project_id: z.string().uuid(),
    ...revenueSourceFields,
})
    .strict();
/** PATCH /orgs/:orgId/revenue-sources/:id — any subset, at least one field. */
export const updateRevenueSourceSchema = z
    .object({
    ...revenueSourceFields,
    amount_received: z.number().nullable().optional(),
    status: revenueSourceStatusSchema,
})
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, {
    message: "At least one field must be provided",
});
//# sourceMappingURL=revenueSources.js.map